'use client'

import { Box, Flex, Text, UnstyledButton } from '@mantine/core'
import { type DataTableSortStatus } from 'mantine-datatable'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { GTM_EVENTS } from '@/configs/dataTrackingEvent'
import type { CoinCategory } from '@/services/category'
import { trackGtmEvent } from '@/services/dataTracking'
import type { CryptoBasket } from '@/types/invest'

import type { KnownSearchParams } from '../../page'
import type { TableRecord } from '../CoinsTable/Table/columns/configs'
import BasketsTable from './BasketsTable'
import CategoriesTable from './CategoriesTable'
import CoinsTable from './CoinsTable'
import SortMenu from './SortMenu'
import styles from './style.module.css'

const TABS = [
  { id: 'allCoins', labelKey: 'HomePageV2.Prediction.all' },
  { id: 'baskets', labelKey: 'baskets.title' },
  { id: 'categories', labelKey: 'category.categories' },
] as const

type TabId = (typeof TABS)[number]['id']

const SORT_OPTIONS = [
  {
    value: '24hVolume',
    labelKey: 'HomePageV2.coinsTable.24hVolume',
    sortStatus: {
      columnAccessor: 'volume',
      sortKey: 'usd_volume_24h',
      direction: 'desc',
    } as DataTableSortStatus<TableRecord>,
  },
  {
    value: '24hChange',
    labelKey: 'HomePageV2.coinsTable.24hChange',
    sortStatus: {
      columnAccessor: '24hChange',
      sortKey: 'usd_price_change_24h',
      direction: 'desc',
    } as DataTableSortStatus<TableRecord>,
  },
  {
    value: 'marketCap',
    labelKey: 'HomePageV2.coinsTable.marketCap',
    sortStatus: {
      columnAccessor: 'marketCap',
      sortKey: 'usd_marketcap',
      direction: 'desc',
    } as DataTableSortStatus<TableRecord>,
  },
  {
    value: 'price',
    labelKey: 'coinTable.headers.price',
    sortStatus: {
      columnAccessor: 'price',
      sortKey: 'usd_price',
      direction: 'desc',
    } as DataTableSortStatus<TableRecord>,
  },
] as const

const CATEGORY_SORT_OPTIONS = [
  {
    value: '24hVolume',
    labelKey: 'HomePageV2.coinsTable.24hVolume',
    sortKey: 'volume_24h_native',
  },
  {
    value: '24hChange',
    labelKey: 'HomePageV2.coinsTable.24hChange',
    sortKey: 'percent_change_24h_native',
  },
  {
    value: 'marketCap',
    labelKey: 'HomePageV2.coinsTable.marketCap',
    sortKey: 'market_cap_native',
  },
] as const

const BASKET_SORT_OPTIONS = [
  {
    value: '3Years',
    labelKey: 'baskets.periods.3Years',
  },
] as const

const SORT_DEFAULTS: Record<TabId, string> = {
  allCoins: SORT_OPTIONS[0].value,
  categories: CATEGORY_SORT_OPTIONS[0].value,
  baskets: BASKET_SORT_OPTIONS[0].value,
}

interface CryptoMarketProps {
  ssrSearchParams: KnownSearchParams
  categories: CoinCategory[]
  baskets: CryptoBasket[]
}

export default function CryptoMarket({ ssrSearchParams, categories, baskets }: CryptoMarketProps) {
  const t = useTranslations()
  const [activeTab, setActiveTab] = useState<TabId>('allCoins')
  const [sortBy, setSortBy] = useState<string>(SORT_DEFAULTS.allCoins)

  const sortOptions =
    activeTab === 'baskets'
      ? BASKET_SORT_OPTIONS
      : activeTab === 'categories'
        ? CATEGORY_SORT_OPTIONS
        : SORT_OPTIONS
  const activeSort = sortOptions.find(option => option.value === sortBy) ?? sortOptions[0]

  return (
    <Box w="100%">
      <Text variant="heading1" fz={28} lh="40px" c="#F7F9FA" component="h2" m={0}>
        {t('common.coinCategoryTypes.crypto')}
      </Text>

      <Flex className={styles.controls} mt={16} mb={16}>
        <Flex gap={12} wrap="nowrap" className={styles.tabs}>
          {TABS.map(tab => (
            <UnstyledButton
              key={tab.id}
              className={`${styles.chip} ${activeTab === tab.id ? styles.chipSelected : ''}`}
              onClick={() => {
                setActiveTab(tab.id)
                setSortBy(SORT_DEFAULTS[tab.id])
                trackGtmEvent({
                  event: GTM_EVENTS.CryptoFilterClicked,
                  filter_name: tab.id,
                })
              }}
            >
              {t(tab.labelKey)}
            </UnstyledButton>
          ))}
        </Flex>

        {activeTab !== 'baskets' && (
          <Box className={styles.sortControl}>
            <SortMenu
              options={sortOptions}
              activeValue={activeSort.value}
              onChange={value => {
                trackGtmEvent({
                  event: GTM_EVENTS.CryptoSortClicked,
                  sort_option: value,
                })
                setSortBy(value)
              }}
            />
          </Box>
        )}
      </Flex>

      {activeTab === 'allCoins' && (
        <CoinsTable
          key={sortBy}
          ssrSearchParams={{ ...ssrSearchParams, page: undefined }}
          initialSortStatus={
            (SORT_OPTIONS.find(o => o.value === sortBy) ?? SORT_OPTIONS[0]).sortStatus
          }
        />
      )}
      {activeTab === 'categories' && (
        <CategoriesTable
          data={categories}
          sortKey={
            (CATEGORY_SORT_OPTIONS.find(o => o.value === sortBy) ?? CATEGORY_SORT_OPTIONS[0])
              .sortKey
          }
        />
      )}
      {activeTab === 'baskets' && <BasketsTable baskets={baskets} />}
    </Box>
  )
}
